/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2016, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * Service to handle our activity model and it's corresponding restfull calls in one place.
 */
angular.module('bulbApp.activityModule')
  .service('BulbActivityService', ['$interval', '$modalStack', '$q', '$rootScope', '$state', '$translate', '$window',
    'Restangular', 'BulbAuthService', 'BulbCommentService', 'BulbErrorService',
    'BulbModalService', 'ErrorService', 'ACTIVITY', 'CONFIG', BulbActivityService]);

function BulbActivityService(
  $interval,
  $modalStack,
  $q,
  $rootScope,
  $state,
  $translate,
  $window,
  Restangular,
  BulbAuthService,
  BulbCommentService,
  BulbErrorService,
  BulbModalService,
  ErrorService,
  ACTIVITY,
  CONFIG,
) {
  const that = this;

  /** Local versions of model variables, needed for cases where we update the model before we update the ui. */
  let localLastViewed; let
    localNumNewActivities;

  /** Stats of ui elements we need to keep track of. */
  let activeTab; let
    headerOpen;

  /** Cached translations. */
  let translations;

  /** Posting state, to ensure we don't double post. */
  let isPosting = false;

  /** Whether user network is stable and available or not */
  let isNetworkAvailable = true;

  let hasSessionInactivityShown = false;

  let hasSessionExpiredShown = false;

  let lastPollingTime;

  /**
  * The instance of the model.
  */
  const instance = {
    activityFeed: {
      lastViewed: null,
      numNewActivities: null,
      activities: [],
    },
    isOnline: function isOnline(networkAvailable) {
      isUserOnline(networkAvailable);
    },
  };

  /**
  * Get and cache the translations we are interested in.
  */
  translations = $translate(['activityfeed_question', 'activityfeed_question_null', 'activityfeed_account_null',
    'activityfeed_unit_null', 'activityfeed_group_null', 'activityfeed_comment_new_link_text',
    'activityfeed_comment_reply_link_text', 'activityfeed_student_private_profile_finished',
    'activityfeed_toggle_private_profiles_completed', 'activityfeed_group_non_member',
    'activityfeed_off_boarding_seniors', 'group_upgrade_from_free_tier_pricing_link']);

  /**
  * Because we have multiple instances of the activity feed (mobile vs desktop) we have multiple states where we want
   * to send lastViewed.
  */
  function canSeeActivityFeed() {
    const validSendStates = [
      { tab: 'ACTIVITY', header: 'general' },
      { tab: 'ACTIVITY_MOBILE', header: 'general-mobile' },
    ];
    const state = validSendStates.find((validState) => validState.tab === activeTab && validState.header === headerOpen);

    if (state) {
      return true;
    }

    return false;
  }

  /**
   * Function to set the value of isNetworkAvailable to true/false based on the user network connection.
   */
  function isUserOnline(networkAvailable) {
    isNetworkAvailable = networkAvailable;
  };

  /**
  * Utility function to the new activities from the server.
  */
  function getActivities() {
    if (!BulbAuthService.currentUser.isAuthenticated()) {
      BulbErrorService.error('Must be authenticated to access activities.');
      stopActivityFeedPolling();
      return;
    }

    const accountId = BulbAuthService.currentUser.id;
    const options = {};
    if (instance.activityFeed.activities.length > 0) {
      options.latestId = instance.activityFeed.activities[0].id;
    }

    if (BulbCommentService.commentable && BulbCommentService.currentPage.unit) {
      const { unit } = BulbCommentService.currentPage;
      /**
      * The reason we are doing this unitType check is to make it so its not possible to send
      * units to the server that are not page units. There is a known race condition that exists where
      * UserUnits are being sent through this method but it is unclear where this could be happening as
      * the only places that units are sent through the comment service happen within page units.
      * Related ticket : BULBAPP-5676
      */
      if (unit.unitType === 'DraftPageUnit'
       || unit.unitType === 'PublishedPageUnit'
       || unit.unitType === 'ArchivePageUnit') {
        options.pageId = unit.id;
      }
    }

    if (lastPollingTime) {
      options.lastPollingTime = lastPollingTime;
    }
    lastPollingTime = new Date();
    if (isNetworkAvailable) {
      Restangular.one('accounts', accountId).customGET('feed', options).then((res) => {
        if (!res) {
          return;
        }

        const { activityFeed } = res;
        const { commentThreads } = res;

        const timeoutFeature = BulbAuthService.currentUser.features.TimeoutFeature;
        const lastUserInput = res.timeOfLastUserAction;
        const date = new Date();
        const FIVE_MIN = 5 * 60 * 1000;
        let TIMEOUT_MIN;

        if (timeoutFeature && timeoutFeature.sessionTimeout > 0 && lastUserInput) {
          TIMEOUT_MIN = timeoutFeature.sessionTimeout * 60 * 1000;
          // Check if the last user input has been more than 5 minutes away from the current time.
          if ((date - new Date(lastUserInput)) >= (TIMEOUT_MIN - FIVE_MIN)) {
            // Show the modal if it hasn't shown yet in the last five minutes.
            if (!hasSessionInactivityShown) {
              showSessionTimeoutModal(true);
              hasSessionInactivityShown = true;
            }
          }

          if ((date - new Date(lastUserInput)) >= TIMEOUT_MIN) {
            if (!hasSessionExpiredShown) {
              $modalStack.dismissAll();
              showSessionTimeoutModal(false);
              hasSessionExpiredShown = true;
            }
          }
        }

        if (activityFeed) {
          localLastViewed = activityFeed.lastViewed;
          localNumNewActivities = activityFeed.numNewActivities;

          // If there are new activities re-fetch the currentUser so translateActivities has all data needed.
          BulbAuthService.fetchCurrentUser().then(() => {
            translateActivities(activityFeed.activities);
          });

          instance.activityFeed.activities = activityFeed.activities.concat(instance.activityFeed.activities);

          // prune to at most the max number of activities
          if (instance.activityFeed.activities.length > CONFIG.activities.max) {
            instance.activityFeed.activities = instance.activityFeed.activities.slice(0, CONFIG.activities.max - 1);
          }

          // update the local lastViewed and numNewActivities if we can't see the activity tab currently,
          // otherwise updateLastViewed, since we are currently looking at the activites.
          if (canSeeActivityFeed()) {
            updateLastViewed();
          } else {
            instance.activityFeed.lastViewed = localLastViewed;
            instance.activityFeed.numNewActivities = localNumNewActivities;
          }
        }
        if (commentThreads) {
          BulbCommentService.updateCommentThreadMap(commentThreads);
        }
      }, (error) => {
        // Something has gone wrong with the polling, so we don't want to keep having the error occur over
        // and over, so we will capture the error and stop our polling.
        ErrorService.captureRestangularError(error);
        stopActivityFeedPolling();
      });
    }
  }

  /**
  * Stop the polling for new activities.
  */
  function stopActivityFeedPolling() {
    if (instance.intervalHandler) {
      $interval.cancel(instance.intervalHandler);
      delete instance.intervalHandler;
    }
  }

  /**
  * Utility function to dynamically get the correct translation for an activity, depending on it's corresponding
  * variable's null states.
  */
  function translateActivities(activities) {
    const membershipsList = BulbAuthService.currentUser.groupMemberships;

    activities.forEach((activity) => {
      if (activity.activityType === 'OFF_BOARDING') {
        $q.when(translations, (fetchedTranslations) => {
          activity.offBoardingLinkText = `<a ng-click="activityCtrl.showOffBoardingModal();">${
            // Avoiding duplication of the translation for the word "here" by using an existing definition
            /* jshint -W106 */
            fetchedTranslations.group_upgrade_from_free_tier_pricing_link}</a>`;
        });
      }

      if (activity.userName && activity.displayName) {
        activity.accountText = `<a href="/${activity.userName}">${activity.displayName}</a>`;
      } else {
        $q.when(translations, (fetchedTranslations) => {
          /* jshint -W106 */
          activity.accountText = fetchedTranslations.activityfeed_account_null;
        });
      }

      if (activity.groupUrl && activity.groupName) {
        let membershipFound = false;
        for (let i = 0; i < membershipsList.length; i++) {
          if (membershipsList[i].displayName === activity.groupName) {
            membershipFound = true;
            break;
          }
        }

        if (membershipFound || activity.displayName === 'GROUP_INVITE') {
          activity.groupText = `<a href="/g/${activity.groupUrl}">${activity.groupName}</a>`;
        } else {
          $q.when(translations, (fetchedTranslations) => {
            /* jshint -W106 */
            activity.groupText = fetchedTranslations.activityfeed_group_non_member;
          });
        }
      } else {
        $q.when(translations, (fetchedTranslations) => {
          /* jshint -W106 */
          activity.groupText = fetchedTranslations.activityfeed_group_null;
        });
      }

      $q.when(translations, (fetchedTranslations) => {
        /* jshint -W106 */
        if (activity.unitPath && activity.unitName) {
          const url = `/u/${activity.unitPath}`;
          activity.unitText = `<a href="${url}">${activity.unitName}</a>`;

          switch (activity.activityType) {
            case 'COMMENT_NEW':
              activity.commentText = `<a href="${url}/comment/${activity.commentThreadId}">${
                fetchedTranslations.activityfeed_comment_new_link_text}</a>`;
              break;
            case 'COMMENT_REPLY':
              activity.commentText = `<a href="${url}/comment/${activity.commentThreadId}">${
                fetchedTranslations.activityfeed_comment_reply_link_text}</a>`;
              break;
          }
        } else {
          activity.unitText = fetchedTranslations.activityfeed_unit_null;

          switch (activity.activityType) {
            case 'COMMENT_NEW':
              activity.commentText = fetchedTranslations.activityfeed_comment_new_link_text;
              break;
            case 'COMMENT_REPLY':
              activity.commentText = fetchedTranslations.activityfeed_comment_reply_link_text;
              break;
          }
        }
        if (activity.collectionUnitPath && activity.collectionUnitName) {
          activity.collectionText = `<a href="/u/${activity.collectionUnitPath}">${activity.collectionUnitName}</a>`;
        } else {
          $q.when(translations, (fetchedTranslations) => {
            /* jshint -W106 */
            activity.collectionText = fetchedTranslations.activityfeed_collection_null;
          });
        }
      });

      $q.when(translations, (fetchedTranslations) => {
        if (activity.questionId && activity.unitPath) {
          /* jshint -W106 */
          activity.questionText = `<a href="/u/${activity.unitPath}#${activity.questionId}">${
            fetchedTranslations.activityfeed_question}</a>`;
        } else {
          /* jshint -W106 */
          activity.questionText = fetchedTranslations.activityfeed_question_null;
        }
      });
    });
  }

  /**
  * Function to display the modal that will show if a session is about to be closed due to inactivity.
  * @param firstWarning boolean that will state whether or not this is the first warning or the final one.
  */
  function showSessionTimeoutModal(firstWarning) {
    if (firstWarning) {
      // If this is the first warning of the session, show an inactivity warning instead of forcibly logging
      // out the user. This will give them a chance to get active again.
      BulbModalService.show({
        titleCode: 'session_inactivity_modal_title',
        bodyText: $translate.instant('session_inactivity_modal_text'),
        okButtonCode: 'button_ok',
        fontAwesomeCode: 'far fa-hourglass',
        windowClass: 'updated-modal-design one-button-modal-design disable-modal-close',
        showCancelButton: false,
        closeOnClick: false,
        backdrop: 'static',
      }).then(() => {
        // There is only an "OK" button which cancels the timeout, just refresh the user account to send the
        // ping to the server.
        BulbAuthService.fetchCurrentUser();
        hasSessionInactivityShown = false;
      });
    } else {
      // If this is not the first warning, it means the inactivity modal has been shown and not clicked. So show
      // the second modal here.

      // We need to make sure that users can leave the page without the publish now / later popup and then
      // we want to clear the auth within the session.
      $rootScope.$broadcast('navigationAwayApproval', true);
      BulbAuthService.signOut(null, true);
      BulbAuthService.fetchCurrentUser();

      BulbModalService.show({
        titleCode: 'session_expired_modal_title',
        bodyText: $translate.instant('session_expired_modal_text'),
        okButtonCode: 'sign_in_button',
        fontAwesomeCode: 'far fa-hourglass',
        windowClass: 'updated-modal-design one-button-modal-design disable-modal-close',
        showCancelButton: false,
        closeOnClick: false,
        backdrop: 'static',
      }).then(() => {
        // Upon clicking the only button on the modal (the sign-in button), take the user to the sign-in page

        // This will redirect the user back to where they came from after they sign back in, we need to force a
        // reload though so that the auth on the client side gets refreshed or this will simply navigate the
        // user back to where they came from with then hit 'sign-in' on the modal.
        $state.go('signIn', { targetUrl: $window.location.href }, { reload: true });
        that.hasSessionExpiredShown = false;
      });
    }
  }

  /**
  * Utility function to send the lastViewed date.
  */
  function updateLastViewed() {
    if (!BulbAuthService.currentUser.isAuthenticated() || that.switchedUser) {
      BulbErrorService.error('Must be authenticated to update the activity feed.');
      return;
    }

    if (localNumNewActivities > 0) {
      const accountId = BulbAuthService.currentUser.id;
      const lastViewedActivity = instance.activityFeed.activities[0];
      const postBody = {
        id: lastViewedActivity.id,
      };

      if (!isPosting) {
        isPosting = true;
        Restangular.one('accounts', accountId).customPOST(postBody, 'feed').then(() => {
          // We need to manually reset the lastViewed and numNewActivities on the client in case new activities have
          // come in on the server between the time we closed the activity tab and getActivities is called.
          instance.activityFeed.numNewActivities = 0;
          localNumNewActivities = 0;
          localLastViewed = lastViewedActivity.date;
        }, (error) => {
          BulbErrorService.error('Cannot fetch update feed', error);
        }).finally(() => {
          isPosting = false;
        });
      }
    }
  }

  /**
  * Event handler to detect the tab ui state.
  */
  $rootScope.$on('headerTabSelected', ($event, tab) => {
    activeTab = tab;

    if (canSeeActivityFeed()) {
      updateLastViewed();
    } else {
      // update local lastViewed
      instance.activityFeed.lastViewed = localLastViewed;
    }
  });

  /**
  * Event handler to detect the main header ui state.
  */
  $rootScope.$on('headerOpened', ($event, headerOption) => {
    headerOpen = headerOption;

    if (canSeeActivityFeed()) {
      updateLastViewed();
    }
  });

  $rootScope.$on('headerClosed', () => {
    headerOpen = false;

    // update local lastViewed
    instance.activityFeed.lastViewed = localLastViewed;
  });

  // Start up the service and loop checking for activities
  if (typeof instance.intervalHandler === 'undefined' && BulbAuthService.currentUser.isAuthenticated()) {
    // Start activity polling interval
    instance.intervalHandler = $interval(() => {
      getActivities();
    }, ACTIVITY.CHECK_INTERVAL);

    // When focus is on tab w/ app poll for activities every 60 seconds
    $window.addEventListener('focus', () => {
      stopActivityFeedPolling();
      instance.intervalHandler = $interval(() => {
        getActivities();
      }, ACTIVITY.CHECK_INTERVAL);
    });

    // When focus is lost from the app stop polling for activities
    $window.addEventListener('blur', () => {
      stopActivityFeedPolling();
    });

    // but go ahead and immediately call it now rather than waiting for the first call...
    getActivities();
  }

  return instance;
}
